"use client";

import Image from "next/image";
import { IconChevronDown } from "@/components/icons";
import { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { getUserDisplayName } from "@/lib/user-display";
import { cn } from "@/lib/utils";
import { LanguageSwitcher } from "@/components/i18n/LanguageSwitcher";
import { LocalizedLink as Link } from "@/components/i18n/LocalizedLink";
import dynamic from "next/dynamic";

// Only quick-registered users ever see it; loading it on demand keeps the
// Freja/QR flow out of every public page's JavaScript.
const VerificationDialog = dynamic(
  () =>
    import("@/features/auth/components/VerificationDialog").then(
      (mod) => mod.VerificationDialog,
    ),
  { ssr: false },
);
import { useI18n } from "@/i18n/I18nProvider";
import { localizedText } from "@/i18n/text";
import {
  MobileNav,
  MobileNavHeader,
  MobileNavMenu,
  MobileNavToggle,
  NavBody,
  NavItems,
  Navbar,
  type NavbarItem,
} from "@/components/ui/resizable-navbar";

type NavItem = NavbarItem;

export default function SiteHeader() {
  const { user, logout, isLoading } = useAuth();
  const { locale, localizedHref, t } = useI18n();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false);
  const [isVerifyDialogOpen, setIsVerifyDialogOpen] = useState(false);
  const accountMenuRef = useRef<HTMLDivElement | null>(null);

  const currentUser = user;
  const userType = currentUser?.accountType;
  const isQuickRegister = userType === "quick_register";
  const roleLabel =
    userType === "student"
      ? t("siteHeader.roles.student")
      : isQuickRegister
        ? localizedText(locale, "Quick-register konto", "Quick-register account")
        : userType === "private_landlord" || userType === "company"
          ? t("siteHeader.roles.company")
          : null;
  const displayName = getUserDisplayName(currentUser);

  const publicNavItems = useMemo<NavItem[]>(
    () => [
      { name: t("siteHeader.nav.home"), link: localizedHref("/") },
      { name: t("siteHeader.nav.housing"), link: localizedHref("/housing") },
      { name: t("siteHeader.nav.allQueues"), link: localizedHref("/all-queues") },
      { name: t("siteHeader.nav.cities"), link: localizedHref("/cities") },
    ],
    [localizedHref, t],
  );

  const studentNavItems = useMemo<NavItem[]>(
    () => [
      {
        name: t("siteHeader.nav.housing"),
        link: localizedHref("/housing"),
        dropdown: [
          { name: t("siteHeader.nav.searchHousing"), link: localizedHref("/housing") },
          { name: t("siteHeader.nav.applications"), link: localizedHref("/applications") },
          { name: t("siteHeader.nav.saved"), link: localizedHref("/saved") },
        ],
      },
      {
        name: t("siteHeader.nav.allQueues"),
        link: localizedHref("/all-queues"),
        dropdown: [
          { name: t("siteHeader.nav.addQueues"), link: localizedHref("/all-queues") },
          { name: t("siteHeader.nav.myQueues"), link: localizedHref("/queues") },
        ],
      },
      { name: t("siteHeader.nav.cities"), link: localizedHref("/cities") },
      { name: t("siteHeader.nav.notifications"), link: localizedHref("/notifications") },
    ],
    [localizedHref, t],
  );

  let navItems = publicNavItems;

  // Company users manage their listings in the portal, so they get the plain signed-in nav here.
  if (userType === "student") {
    navItems = studentNavItems;
  } else if (currentUser && !isQuickRegister) {
    navItems = [
      { name: t("siteHeader.nav.housingSearch"), link: localizedHref("/housing") },
      { name: t("siteHeader.nav.cities"), link: localizedHref("/cities") },
    ];
  }

  let accountMenuItems: NavItem[] = [];

  if (userType === "student") {
    accountMenuItems = [
      { name: t("siteHeader.account.myAccount"), link: localizedHref("/account") },
      { name: t("siteHeader.account.faq"), link: localizedHref("/faq") },
    ];
  } else if (isQuickRegister) {
    accountMenuItems = [
      { name: t("siteHeader.account.myAccount"), link: localizedHref("/account") },
      { name: t("siteHeader.account.faq"), link: localizedHref("/faq") },
    ];
  } else if (userType === "private_landlord" || userType === "company") {
    accountMenuItems = [
      { name: t("siteHeader.account.myAccount"), link: localizedHref("/account") },
      { name: t("siteHeader.account.faq"), link: localizedHref("/faq") },
    ];
  } else if (currentUser) {
    accountMenuItems = [
      { name: t("siteHeader.account.myAccount"), link: localizedHref("/account") },
    ];
  }

  const closeMenus = () => {
    setIsMobileMenuOpen(false);
    setIsAccountMenuOpen(false);
  };

  const handleMobileToggle = () => {
    setIsAccountMenuOpen(false);
    setIsMobileMenuOpen((prev) => !prev);
  };

  const handleAccountToggle = () => {
    setIsMobileMenuOpen(false);
    setIsAccountMenuOpen((prev) => !prev);
  };

  const handleLogout = () => {
    logout();
    closeMenus();
  };

  const openVerifyDialog = () => {
    closeMenus();
    setIsVerifyDialogOpen(true);
  };

  // A same-device e-ID launch returns to the current page with this hash (see
  // buildCurrentPageFrejaReturnUrl in VerificationDialog); reopen the dialog
  // so the resumed session's polling picks the verification back up. The
  // header owns this — it is on every page, and only one dialog instance may
  // auto-open.
  useEffect(() => {
    if (!isQuickRegister) return;
    if (window.location.hash === "#verify-account") {
      setIsVerifyDialogOpen(true);
    }
  }, [isQuickRegister]);

  useEffect(() => {
    if (!isAccountMenuOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        accountMenuRef.current &&
        !accountMenuRef.current.contains(event.target as Node)
      ) {
        setIsAccountMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isAccountMenuOpen]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeMenus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  useEffect(() => {
    const originalOverflow = document.body.style.overflow;

    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isMobileMenuOpen]);

  if (isLoading) {
    return (
      <Navbar className="top-4 opacity-0">
        <NavBody>
          <div className="h-10" />
        </NavBody>
      </Navbar>
    );
  }

  return (
    <Navbar className="top-4">
      <NavBody>
        <Link
          href="/"
          className="relative z-20 flex items-center gap-2 px-2 py-1 text-sm font-medium"
        >
          <Image
            src="/campuslyan-logo.svg"
            alt=""
            width={30}
            height={30}
            priority
          />
          <div className="leading-tight">
            <span className="text-base">CampusLyan</span>
          </div>
        </Link>

        <NavItems items={navItems} onItemClick={() => setIsAccountMenuOpen(false)} />

        <div className="relative z-20 hidden items-center gap-2 xl:flex">
          <LanguageSwitcher compact />
          {!currentUser ? (
            <>
              <Link
                href="/login"
                className="inline-flex rounded-full px-4 py-2 text-sm font-medium text-neutral-700 transition hover:bg-neutral-100"
              >
                {t("siteHeader.auth.login")}
              </Link>
              <Link
                href="/register"
                className="inline-flex rounded-full bg-brand px-5 py-2 text-sm font-semibold text-white transition hover:bg-[#00341d]"
              >
                {t("siteHeader.auth.createAccount")}
              </Link>
            </>
          ) : (
            <>
              {isQuickRegister ? (
                <button
                  type="button"
                  onClick={openVerifyDialog}
                  className="inline-flex rounded-full bg-[#3E3A93] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#302d78] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#3E3A93]"
                >
                  {localizedText(locale, "Verifiera nu", "Verify now")}
                </button>
              ) : null}
              <div
                ref={accountMenuRef}
                className="relative"
                onKeyDown={(event) => {
                  if (event.key === "Escape") {
                    setIsAccountMenuOpen(false);
                  }
                }}
              >
                <button
                  type="button"
                  onClick={handleAccountToggle}
                  aria-haspopup="menu"
                  aria-expanded={isAccountMenuOpen}
                  className="flex items-center gap-2 rounded-full border border-neutral-200 bg-white px-4 py-2 text-sm text-neutral-700 transition hover:border-neutral-300 hover:bg-neutral-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
                >
                  <div className="hidden max-w-32 sm:block">
                    <p className="truncate text-left text-sm font-medium text-neutral-900">
                      {displayName}
                    </p>
                  </div>
                  <IconChevronDown
                    className={cn(
                      "h-4 w-4 text-neutral-400 transition-transform",
                      isAccountMenuOpen && "rotate-180",
                    )}
                  />
                </button>

                {isAccountMenuOpen && (
                  <div className="absolute right-0 top-[calc(100%+0.5rem)] z-50 w-64 rounded-2xl border border-neutral-200 bg-white p-2 shadow-[0_12px_30px_rgba(15,23,42,0.08)] animate-dropdown">
                    <div className="rounded-xl bg-neutral-50 px-3 py-3">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-neutral-950">
                          {displayName}
                        </p>
                        <p className="truncate text-xs text-neutral-500">
                          {roleLabel ?? currentUser.email}
                        </p>
                      </div>
                    </div>

                    <div className="mt-2 grid gap-1">
                      {accountMenuItems.map((item) => (
                        <Link
                          key={item.link}
                          href={item.link}
                          onClick={() => setIsAccountMenuOpen(false)}
                          className="rounded-xl px-3 py-2.5 text-sm font-medium text-neutral-700 transition hover:bg-neutral-50 hover:text-neutral-950"
                        >
                          {item.name}
                        </Link>
                      ))}
                    </div>

                    <button
                      type="button"
                      onClick={handleLogout}
                      className="mt-2 w-full rounded-xl px-3 py-2.5 text-left text-sm font-medium text-red-600 transition hover:bg-red-50"
                    >
                      {t("siteHeader.auth.logout")}
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </NavBody>

      <MobileNav>
        <MobileNavHeader>
          <Link href="/" className="flex items-center gap-2">
            <Image
              src="/campuslyan-logo.svg"
              alt=""
              width={26}
              height={26}
            />
            <span className="text-sm font-semibold">CampusLyan</span>
          </Link>
          <div className="flex items-center gap-2">
            <LanguageSwitcher compact />
            <MobileNavToggle
              isOpen={isMobileMenuOpen}
              onClick={handleMobileToggle}
            />
          </div>
        </MobileNavHeader>

        <MobileNavMenu isOpen={isMobileMenuOpen} onClose={() => setIsMobileMenuOpen(false)}>
          <div className="flex w-full flex-col gap-3">
            {navItems.map((item) => (
              <div key={item.name}>
                <Link
                  href={item.link}
                  onClick={closeMenus}
                  className="text-base text-neutral-700"
                >
                  {item.name}
                </Link>

                {item.dropdown && item.dropdown.length > 0 && (
                  <div className="mt-2 flex flex-col gap-2 border-l-2 border-neutral-100 pl-4">
                    {item.dropdown.map((subItem) => (
                      <Link
                        key={subItem.link}
                        href={subItem.link}
                        onClick={closeMenus}
                        className="text-sm text-neutral-500"
                      >
                        {subItem.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}

            {!currentUser ? (
              <>
                <Link
                  href="/login"
                  onClick={closeMenus}
                  className="mt-2 inline-flex w-full items-center justify-center rounded-full border border-neutral-200 px-5 py-3 text-base font-medium text-neutral-700 transition hover:bg-neutral-50"
                >
                  {t("siteHeader.auth.login")}
                </Link>
                <Link
                  href="/register"
                  onClick={closeMenus}
                  className="inline-flex w-full items-center justify-center rounded-full bg-brand px-5 py-3 text-base font-semibold text-white transition hover:bg-[#00341d]"
                >
                  {t("siteHeader.auth.createAccount")}
                </Link>
              </>
            ) : (
              <div className="mt-2 w-full border-t border-neutral-200 pt-4">
                {isQuickRegister ? (
                  <button
                    type="button"
                    onClick={openVerifyDialog}
                    className="mb-3 inline-flex w-full items-center justify-center rounded-full bg-[#3E3A93] px-5 py-3 text-base font-semibold text-white transition hover:bg-[#302d78]"
                  >
                    {localizedText(locale, "Verifiera nu", "Verify now")}
                  </button>
                ) : null}
                <div className="mb-3 px-1">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-neutral-950">
                      {displayName}
                    </p>
                    <p className="truncate text-xs text-neutral-500">
                      {roleLabel ?? currentUser.email}
                    </p>
                  </div>
                </div>

                <div className="flex flex-col gap-1">
                  {accountMenuItems.map((item) => (
                    <Link
                      key={item.link}
                      href={item.link}
                      onClick={closeMenus}
                      className="rounded-xl px-3 py-2.5 text-sm text-neutral-700 transition hover:bg-neutral-50"
                    >
                      {item.name}
                    </Link>
                  ))}
                </div>

                <button
                  type="button"
                  onClick={handleLogout}
                  className="mt-1 w-full rounded-xl px-3 py-2.5 text-left text-sm font-medium text-red-600 transition hover:bg-red-50"
                >
                  {t("siteHeader.auth.logout")}
                </button>
              </div>
            )}
          </div>
        </MobileNavMenu>
      </MobileNav>

      {isQuickRegister ? (
        <VerificationDialog
          open={isVerifyDialogOpen}
          onOpenChange={setIsVerifyDialogOpen}
        />
      ) : null}
    </Navbar>
  );
}
